/**
 * TravelResultCard — the /travel command's card.
 *
 * Fetches the calendar scan's travelTrips (discovery-status) live on render
 * and shows the TravelReport. The marker block persists with the message, so
 * the card re-fetches fresh data on reload.
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/apiClient';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { TravelReport } from '../TravelReport';
import type { TripRow } from '../TravelReport';

interface DiscoveryStatusSlim {
  jobs?: { calendar?: { travelTrips?: TripRow[] } };
}

export function TravelResultCard() {
  const [trips, setTrips] = useState<TripRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const { token } = useSession();

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const wsId = getActiveWorkspaceId();
        const data = await apiFetch<DiscoveryStatusSlim>(
          `/workspaces/${wsId}/onboarding/discovery-status`,
          { authToken: token },
        );
        const raw = data?.jobs?.calendar?.travelTrips ?? [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (alive) {
          setTrips(raw.map((t) => ({
            ...t,
            planned: new Date(t.endISO ?? t.startISO ?? 0).getTime() >= today.getTime(),
          })));
        }
      } catch {
        if (alive) setFailed(true);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (failed) {
    return <p>Travel data isn&apos;t available yet — connect your calendar and let a sync finish.</p>;
  }
  if (!trips) return null;
  if (trips.length === 0) return <p>No trips found in your calendar over the last year.</p>;
  return <TravelReport trips={trips} />;
}
